import logoSvg from "@/assets/favicon.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { getEnabledExternalLinks } from "@/lib/external-links";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "@tanstack/react-router";
import {
  Code2,
  CreditCard,
  ExternalLink,
  FileText,
  Home,
  Key,
  LogOut,
  Menu,
  Settings,
  User,
  X
} from "lucide-react";
import { ReactNode, useState, useEffect, useRef } from "react";
import { Logo } from "@/components/ui/logo";
import { SocialIcon } from "react-social-icons";

interface DashboardLayoutProps {
  children: ReactNode;
}

const navigation = [
  { name: "Overview", href: "/dashboard", icon: Home },
  { name: "Playground", href: "/dashboard/playground", icon: Code2 },
  { name: "API Keys", href: "/dashboard/api-keys", icon: Key },
  { name: "Billing", href: "/dashboard/billing", icon: CreditCard },
  { name: "Settings", href: "/dashboard/settings", icon: Settings },
];

const externalLinks = [
  { name: "Documentation", href: "https://plusefin.com/docs/", icon: FileText },
  { name: "MCP Tools", href: "https://plusefin.com/docs/tools/", icon: ExternalLink },
];

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarExpanded, setDesktopSidebarExpanded] = useState(false);

  const handleSignOut = async () => {
    await signOut();
  };

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile only
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [sidebarOpen]);

  // Get enabled external links from configuration
  const enabledExternalLinks = getEnabledExternalLinks();

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#f7f3ea]">
        {/* Mobile sidebar backdrop - only on mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile sidebar */}
        <div className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-[#fbf7ef] shadow-2xl shadow-stone-950/10 transition-transform duration-300 ease-in-out lg:hidden",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          <div className="flex flex-col flex-grow">
            {/* Mobile header with close button */}
            <div className="flex h-16 items-center justify-between px-4">
              <Link to="/dashboard" className="flex items-center space-x-2">
                <img src={logoSvg} alt="PlusE Finance" className="w-8 h-8" />
                <span className="text-xl font-bold">PlusE Finance</span>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <SidebarContent
              currentPath={location.pathname}
              onSignOut={handleSignOut}
              expanded
            />
          </div>
        </div>

        {/* Desktop sidebar */}
        <div
          className={cn(
            "hidden lg:fixed lg:inset-y-0 lg:z-50 lg:mt-[60px] lg:flex lg:flex-col lg:border-r lg:border-stone-200/80 lg:bg-white/85 lg:backdrop-blur-xl lg:transition-[width] lg:duration-200 lg:ease-out",
            desktopSidebarExpanded ? "lg:w-60" : "lg:w-[72px]"
          )}
          onMouseEnter={() => setDesktopSidebarExpanded(true)}
          onMouseLeave={() => setDesktopSidebarExpanded(false)}
          onFocus={() => setDesktopSidebarExpanded(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setDesktopSidebarExpanded(false);
            }
          }}
        >
          <div className="flex flex-grow flex-col overflow-hidden">
            <SidebarContent
              currentPath={location.pathname}
              onSignOut={handleSignOut}
              expanded={desktopSidebarExpanded}
            />
          </div>
        </div>

        {/* Main content */}
        <div className={cn(
          "transition-[padding] duration-200 ease-out",
          desktopSidebarExpanded ? "lg:pl-60" : "lg:pl-[72px]"
        )}>
          {/* Top bar */}
          <div className="lg:hidden sticky top-0 z-30 flex h-16 shrink-0 items-center gap-x-4 border-b border-stone-200/80 bg-white/85 px-4 shadow-sm backdrop-blur-xl sm:gap-x-6 sm:px-6 lg:px-8">
            {/* Mobile menu button - only visible on mobile */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </Button>
          </div>

          {/* Page content */}
          <main className="py-10 pt-5">
            <div className="px-4 sm:px-6 lg:px-7">{children}</div>
            <div className="mt-10">
              <DashboardSiteFooter />
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider >
  );
}

function DashboardSiteFooter() {
  const ref = useRef<any>(null);

  useEffect(() => {
    const applyDashboardMode = () => {
      if (ref.current) {
        ref.current.isDashboardDomain = true;
        ref.current.sitePath = '//plusefin.com';
        ref.current.currentYear = new Date().getFullYear().toString();
        ref.current.requestUpdate?.();
      }
    };

    if (customElements.get('site-footer')) {
      applyDashboardMode();
      return;
    }

    customElements.whenDefined('site-footer').then(applyDashboardMode);
  }, []);

  return <site-footer ref={ref} sitepath="//plusefin.com" currentyear={new Date().getFullYear().toString()} />;
}

function SidebarContent({
  currentPath,
  onSignOut,
  expanded = false,
}: {
  currentPath: string;
  onSignOut: () => void;
  expanded?: boolean;
}) {
  const { user } = useAuth();
  const labelClassName = expanded
    ? "opacity-100"
    : "max-w-0 overflow-hidden opacity-0";

  return (
    <div className="flex flex-col flex-grow">
      {/* User info */}
      <div className={cn("px-3 py-4", expanded && "px-4")}>
        <Card className="h-16 overflow-hidden rounded-2xl border-stone-200/80 bg-[#f7f1e6] px-3 shadow-none">
          <div className={cn("flex h-full items-center", expanded ? "gap-3" : "justify-center")}>
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.user_metadata?.avatar_url} />
              <AvatarFallback className="bg-emerald-100 text-emerald-700">
                {user?.email?.charAt(0).toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <div className={cn("min-w-0 flex-1 whitespace-nowrap", labelClassName)}>
              <p className="text-sm font-medium text-stone-950">Personal</p>
              <p className="text-xs text-stone-500 truncate">
                {user?.email || "user@example.com"}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Navigation */}
      <nav className={cn("flex-1 space-y-1", expanded ? "px-4" : "px-3")}>
        {navigation.map((item) => {
          const isActive = currentPath === item.href;
          return (
            <Tooltip key={item.name}>
              <TooltipTrigger asChild>
                <Link
                  to={item.href}
                  className={cn(
                    "group flex items-center rounded-full px-3 py-2.5 text-sm font-medium transition-colors",
                    expanded ? "justify-start" : "justify-center",
                    isActive
                      ? "bg-stone-950 text-white shadow-sm"
                      : "text-stone-500 hover:bg-stone-100 hover:text-stone-950"
                  )}
                >
                  <item.icon
                    className={cn(
                      "h-5 w-5 shrink-0 transition-colors",
                      expanded ? "mr-3" : "mr-0",
                      isActive
                        ? "text-white"
                        : "text-stone-400 group-hover:text-stone-600"
                    )}
                  />
                  <span className={cn("whitespace-nowrap", labelClassName)}>{item.name}</span>
                </Link>
              </TooltipTrigger>
              {!expanded && (
                <TooltipContent side="right">
                  <p>{item.name}</p>
                </TooltipContent>
              )}
            </Tooltip>
          );
        })}
      </nav>

      <Separator className="bg-stone-200/80" />

      {/* External links */}
      <div className={cn("py-4", expanded ? "px-4" : "px-3")}>
        <div className="space-y-1">
          {externalLinks.map((item) => (
            <Tooltip key={item.name}>
              <TooltipTrigger asChild>
                <a
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  className={cn(
                    "group flex items-center rounded-full px-3 py-2.5 text-sm font-medium text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-950",
                    expanded ? "justify-start" : "justify-center"
                  )}
                >
                  <item.icon className={cn("h-5 w-5 shrink-0 text-stone-400 group-hover:text-stone-600", expanded ? "mr-3" : "mr-0")} />
                  <span className={cn("whitespace-nowrap", labelClassName)}>{item.name}</span>
                  <ExternalLink className={cn("ml-auto h-4 w-4 text-stone-400", labelClassName)} />
                </a>
              </TooltipTrigger>
              {!expanded && (
                <TooltipContent side="right">
                  <p>Open {item.name}</p>
                </TooltipContent>
              )}
            </Tooltip>
          ))}
        </div>
      </div>

      <div className={cn("mt-auto pb-4", expanded ? "px-4" : "px-3")}>
        <Button
          variant="outline"
          className={cn(
            "w-full rounded-full border-stone-200 bg-white text-stone-600 hover:bg-red-50 hover:text-red-700",
            expanded ? "justify-start" : "justify-center"
          )}
          onClick={onSignOut}
        >
          <LogOut className={cn("h-5 w-5 text-stone-400", expanded ? "mr-3" : "mr-0")} />
          <span className={cn("whitespace-nowrap", labelClassName)}>Logout</span>
        </Button>
      </div>
    </div>
  );
}
